/**
 * Copyright 2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
export class SyncStoreWithBatchedGet {
    constructor() {
        this.operation = 'sync';
    }
}
export class AsyncStoreWithBatchedGet {
    constructor() {
        this.operation = 'async';
    }
}
export const getBatchedSync = (store, keys) => {
    if (store instanceof SyncStoreWithBatchedGet) {
        return store.getBatched(keys);
    }
    return keys.map((key) => store.get(key));
};
export const getBatchedAsync = (store, keys) => {
    if (store instanceof AsyncStoreWithBatchedGet) {
        return store.getBatched(keys);
    }
    return Promise.all(keys.map((key) => store.get(key)));
};
export class SyncPrefixStore extends SyncStoreWithBatchedGet {
    constructor(store, prefix, transformGet, transformSet) {
        super();
        this.operation = 'sync';
        this.store = store;
        this.prefix = prefix;
        this.transformGet = transformGet;
        this.transformSet = transformSet;
    }
    addPrefix(key) {
        return `${this.prefix}${key}`;
    }
    removePrefix(key) {
        return key.substring(this.prefix.length);
    }
    set(key, value) {
        return this.store.set(this.addPrefix(key), this.transformSet(value));
    }
    get(key) {
        const value = this.store.get(this.addPrefix(key));
        return value ? this.transformGet(value) : undefined;
    }
    remove(key) {
        return this.store.remove(this.addPrefix(key));
    }
    getInternalKeys() {
        return this.store.getKeys().filter((key) => key.startsWith(this.prefix));
    }
    getKeys() {
        return this.getInternalKeys().map((key) => this.removePrefix(key));
    }
    getBatched(keys) {
        return getBatchedSync(this.store, keys.map((key) => this.addPrefix(key)))
            .map((value) => value ? this.transformGet(value) : undefined);
    }
}
export class AsyncPrefixStore {
    constructor(cache, prefix, transformGet, transformSet) {
        this.operation = 'async';
        this.cache = cache;
        this.prefix = prefix;
        this.transformGet = transformGet;
        this.transformSet = transformSet;
    }
    addPrefix(key) {
        return `${this.prefix}${key}`;
    }
    removePrefix(key) {
        return key.substring(this.prefix.length);
    }
    set(key, value) {
        return this.cache.set(this.addPrefix(key), this.transformSet(value));
    }
    get(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.cache.get(this.addPrefix(key));
            return value ? this.transformGet(value) : undefined;
        });
    }
    remove(key) {
        return this.cache.remove(this.addPrefix(key));
    }
    getInternalKeys() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.cache.getKeys().then((keys) => keys.filter((key) => key.startsWith(this.prefix)));
        });
    }
    getKeys() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getInternalKeys().then((keys) => keys.map((key) => this.removePrefix(key)));
        });
    }
    getBatched(keys) {
        return __awaiter(this, void 0, void 0, function* () {
            const values = yield getBatchedAsync(this.cache, keys.map((key) => this.addPrefix(key)));
            return values.map((value) => value ? this.transformGet(value) : undefined);
        });
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=store.js.map